import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { unknownPageError } from "@/errors/unrecoverable-error";
import {
  resolveShareLink,
  type SavedViewLocationState,
} from "@/features/saved-views/resolve-share-link";
import { LoadingScreen } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/system";
import { useEffect, type JSX } from "react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * @returns the entry point for a short `/share/<id>` link
 *
 * A share link names a saved view, not a project, so it cannot be opened
 * directly: the view has to be loaded first to learn which project it belongs
 * to. This page does that and then redirects into the viewer, handing the view
 * over through the router location state so the payload is not bound by the
 * browser's url length limit and never shows up in the address bar.
 *
 * The redirect replaces the current entry, so the share link does not sit in the
 * history for the back button to return to.
 *
 * A link that cannot be loaded - an unknown or expired id - shows the same page
 * an unknown url shows. There is no project to fall back into at that point, so
 * the viewer's invalid-link banner is not an option here.
 */
export function ShareLinkPage(): JSX.Element {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { handleErrorWithPage } = useErrorHandlers();

  useEffect(() => {
    if (!shareId) {
      handleErrorWithPage(unknownPageError("The share link has no id"));
      return;
    }

    let isCurrent = true;
    resolveShareLink(shareId)
      .then(({ url, savedView }) => {
        if (!isCurrent) return;
        const state: SavedViewLocationState = { savedView };
        navigate(url, { replace: true, state });
      })
      .catch((error: unknown) => {
        if (isCurrent) handleErrorWithPage(unknownPageError(error));
      });

    return () => {
      isCurrent = false;
    };
  }, [shareId, navigate, handleErrorWithPage]);

  return (
    <Stack
      direction="row"
      sx={{ width: "100%", height: "100%", justifyContent: "center" }}
    >
      <LoadingScreen />
    </Stack>
  );
}
